import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Share2, Printer, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function DiagnosticTopicDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setItem(null); setNotFound(false);
    api.get(`/diagnostic-guide/${slug}`).then(({ data }) => setItem(data)).catch(() => setNotFound(true));
    api.get("/diagnostic-guide").then(({ data }) => setSiblings(data)).catch(() => {});
  }, [slug]);

  const idx = siblings.findIndex((r) => r.slug === slug);
  const prev = idx > 0 ? siblings[idx - 1] : null;
  const next = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: item?.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copiado!"); }
    } catch (_) {
      // user cancelled or share failed silently
    }
  };

  if (notFound) {
    return (
      <div className="container-cuidar py-24 text-center">
        <AlertCircle className="mx-auto h-10 w-10 text-[hsl(var(--muted-foreground))]" />
        <h1 className="mt-4 font-heading text-2xl font-black">Tópico não encontrado</h1>
        <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
          Talvez ele tenha sido movido ou renomeado.
        </p>
        <Button onClick={() => navigate("/guia-do-diagnostico")} className="mt-6 rounded-full">
          Voltar ao Guia do Diagnóstico
        </Button>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="container-cuidar py-14">
        <div className="animate-pulse space-y-3">
          <div className="h-8 w-72 bg-[hsl(var(--muted))] rounded" />
          <div className="h-4 w-64 bg-[hsl(var(--muted))] rounded" />
          <div className="mt-8 h-96 bg-[hsl(var(--muted))] rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 print:hidden">
        <div className="container-cuidar py-8">
          <button
            onClick={() => navigate("/guia-do-diagnostico")}
            className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
            data-testid="diagnostic-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar ao Guia do Diagnóstico
          </button>
          <div className="mt-5 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--secondary))]">
                {item.category}
              </div>
              <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-black" data-testid="diagnostic-title">
                {item.title}
              </h1>
              {item.summary && (
                <p className="mt-3 max-w-2xl text-[hsl(var(--muted-foreground))]">{item.summary}</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={share} className="rounded-full" data-testid="diagnostic-share-btn">
                <Share2 className="h-4 w-4 mr-1.5" /> Compartilhar
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.print()} className="rounded-full" data-testid="diagnostic-print-btn">
                <Printer className="h-4 w-4 mr-1.5" /> Imprimir
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="container-cuidar py-10 grid gap-10 lg:grid-cols-[1fr_260px]">
        <article className="prose prose-neutral max-w-none prose-headings:font-heading prose-headings:font-black prose-h2:mt-10 prose-h2:mb-4 prose-h2:text-2xl prose-h3:mt-6 prose-h3:text-lg prose-p:leading-relaxed prose-li:my-1 prose-strong:text-[hsl(var(--foreground))] prose-a:text-[hsl(var(--primary))]" data-testid="diagnostic-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.content || ""}</ReactMarkdown>
        </article>

        <aside className="print:hidden">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="surface p-5">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Precisa de orientação?</div>
              <div className="mt-2 font-heading font-bold">Fale com nossa equipe pelo WhatsApp</div>
              <Link to="/contato" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[hsl(var(--primary))] font-semibold hover:underline">
                Falar com a equipe <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="surface p-5 bg-[hsl(var(--secondary))]/8 border-[hsl(var(--secondary))]/25">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--secondary))]">Depois do diagnóstico</div>
              <div className="mt-2 font-heading font-bold">Conheça seus direitos</div>
              <p className="text-xs mt-1 text-[hsl(var(--muted-foreground))]">
                Guia completo dos direitos garantidos por lei à sua família.
              </p>
              <Link to="/direitos" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[hsl(var(--primary))] font-semibold hover:underline">
                Ver direitos <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </aside>
      </section>

      <section className="container-cuidar pb-14 print:hidden">
        <div className="grid gap-4 md:grid-cols-2">
          {prev && (
            <Link to={`/guia-do-diagnostico/${prev.slug}`} className="surface p-5 hover:-translate-y-1 hover:shadow-md transition-all flex items-center gap-4">
              <ArrowLeft className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Anterior</div>
                <div className="font-heading font-bold">{prev.title}</div>
              </div>
            </Link>
          )}
          {next && (
            <Link to={`/guia-do-diagnostico/${next.slug}`} className="surface p-5 hover:-translate-y-1 hover:shadow-md transition-all flex items-center justify-between gap-4 md:col-start-2">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Próximo</div>
                <div className="font-heading font-bold">{next.title}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}
